package com.fantasytenis.web.components.skills

import androidx.compose.runtime.Composable
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.skills.entity.HiddenGemPlayer
import com.fantasytenis.shared.domain.skills.entity.SkillLeaderboardEntry
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderTop
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Player skill card for the Skills Hub
 * Shows player name, country, ranking, and all 5 skill bars
 */
@Composable
fun PlayerSkillCard(
    player: TennisPlayerEntity,
    onClick: () -> Unit
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(16.px)
            cursor("pointer")
            border(1.px, LineStyle.Solid, Colors.Border)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        // Header: Name + Country + Ranking
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            // Name and country
            Div {
                Div({
                    style {
                        fontSize(16.px)
                        fontWeight(600)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text(player.displayName)
                }
                Div({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                        marginTop(2.px)
                    }
                }) {
                    Text(player.country)
                }
            }

            // Ranking badge
            player.atpRanking?.let { ranking ->
                Span({
                    style {
                        backgroundColor(Colors.Gray100)
                        color(Colors.TextSecondary)
                        fontSize(12.px)
                        fontWeight(600)
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                    }
                }) {
                    Text("#$ranking")
                }
            }
        }

        // Skill bars
        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            Category.entries.forEach { category ->
                SkillBar(
                    category = category,
                    value = player.getCostForCategory(category),
                    showLabel = true,
                    showTier = false,
                    size = SkillBarSize.SMALL
                )
            }
        }

        // Total cost
        Div({
            style {
                marginTop(12.px)
                paddingTop(12.px)
                borderTop(1.px, LineStyle.Solid, Colors.Border)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("Total Cost")
            }
            Span({
                style {
                    fontSize(18.px)
                    fontWeight(700)
                    color(Colors.Primary)
                }
            }) {
                Text("${player.costs.total}")
            }
        }
    }
}

/**
 * Hidden gem player card with value score badge
 */
@Composable
fun HiddenGemCard(
    player: HiddenGemPlayer,
    onClick: () -> Unit
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(16.px)
            cursor("pointer")
            border(1.px, LineStyle.Solid, Colors.Border)
            position(Position.Relative)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        // Hidden Gem badge
        Div({
            style {
                position(Position.Absolute)
                top((-8).px)
                right((-8).px)
                backgroundColor(Colors.Secondary)
                color(Colors.Black)
                fontSize(10.px)
                fontWeight(700)
                padding(4.px, 8.px)
                borderRadius(4.px)
            }
        }) {
            Text("💎 GEM")
        }

        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            Div {
                Div({
                    style {
                        fontSize(16.px)
                        fontWeight(600)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text(player.playerName)
                }
                Div({
                    style {
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                        marginTop(2.px)
                    }
                }) {
                    Text("${player.country} • #${player.atpRanking ?: "N/A"}")
                }
            }
        }

        // Top skill highlight
        Div({
            style {
                backgroundColor(getCategoryColor(player.topSkill.category))
                color(Colors.White)
                padding(8.px, 12.px)
                borderRadius(8.px)
                marginBottom(12.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Span({
                    style {
                        fontSize(12.px)
                        fontWeight(500)
                    }
                }) {
                    Text("Best: ${player.topSkill.category.displayName}")
                }
                Span({
                    style {
                        fontSize(18.px)
                        fontWeight(700)
                    }
                }) {
                    Text("${player.topSkill.cost}")
                }
            }
        }

        // Average cost
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("Avg Cost")
            }
            Span({
                style {
                    fontSize(16.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                }
            }) {
                Text(player.averageCost.asDynamic().toFixed(1) as String)
            }
        }
    }
}

/**
 * Compact leaderboard entry card
 */
@Composable
fun LeaderboardEntryCard(
    entry: SkillLeaderboardEntry,
    category: Category,
    onClick: () -> Unit
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(8.px)
            padding(12.px)
            cursor("pointer")
            border(1.px, LineStyle.Solid, Colors.Border)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        // Rank
        Div({
            style {
                width(32.px)
                height(32.px)
                borderRadius(50.percent)
                backgroundColor(
                    when (entry.skillRank) {
                        1 -> Colors.Medal.GoldBackground
                        2 -> Colors.Medal.SilverBackground
                        3 -> Colors.Medal.BronzeBackground
                        else -> Colors.Gray200
                    }
                )
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                fontSize(14.px)
                fontWeight(700)
                color(
                    when (entry.skillRank) {
                        1 -> Colors.Medal.GoldText
                        2 -> Colors.Medal.SilverText
                        3 -> Colors.Medal.BronzeText
                        else -> Colors.TextPrimary
                    }
                )
            }
        }) {
            Text("${entry.skillRank}")
        }

        // Player info
        Div({
            style {
                flex(1)
            }
        }) {
            Div({
                style {
                    fontSize(14.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                }
            }) {
                Text(entry.playerName)
            }
            Div({
                style {
                    fontSize(12.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("${entry.country} • #${entry.atpRanking ?: "N/A"}")
            }
        }

        // Cost with trend
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
            }
        }) {
            TrendArrow(entry.trend)
            Span({
                style {
                    fontSize(18.px)
                    fontWeight(700)
                    color(getCategoryColor(category))
                }
            }) {
                Text("${entry.skillCost}")
            }
        }
    }
}
